import type { Exam, ExamState } from '../../exam.js';
import i18next from 'i18next';
import '../../../i18n.js';
import { SUPPORTED_LANGUAGES } from '../../../i18n.js';
import {
  confirmSplitInChatToolDef,
  confirmSplitInChatAvailability,
  confirmStructureInChatToolDef,
  confirmStructureInChatAvailability,
  createExamToolDef,
  createExamAvailability,
  handleCreateExam,
  confirmExamCreationToolDef,
  confirmExamCreationAvailability,
  handleConfirmExamCreation,
  gradingAgentToolDef,
  gradingAgentAvailability,
  setTaskScoreToolDef,
  setTaskScoreAvailability,
  handleSetTaskScore,
  gradingSchemeToolDef,
  gradingSchemeAvailability,
  handleConfirmSplitInChat,
  handleConfirmStructureInChat,
  handleGenerateGradingScheme,
  handleGenerateMasterSolution,
  handleMetadata,
  handleOpenCanvas,
  handleGetTaskTypes,
  handleProcessFiles,
  handleQueryTool,
  handleRefineGradingScheme,
  handleRefineMasterSolution,
  handleRunGradingAgent,
  handleSendAssignmentMessage,
  handleUpdateOpenTaskContent,
  masterSolutionAvailability,
  metadataAvailability,
  metadataToolDef,
  masterSolutionToolDef,
  openCanvasAvailability,
  openCanvasToolDef,
  processFilesAvailability,
  processFilesToolDef,
  PROCESS_FILES_TOOL_NAME,
  queryToolAvailability,
  queryToolsDef,
  taskTypeToolAvailability,
  taskTypeToolDef,
  refineGradingSchemeAvailability,
  refineGradingSchemeToolDef,
  refineMasterSolutionAvailability,
  refineMasterSolutionToolDef,
  saveTeacherMemoryAvailability,
  saveTeacherMemoryToolDef,
  handleSaveTeacherMemory,
  getTeacherMemoryToolDef,
  handleGetTeacherMemory,
  getTeacherMemoryAvailability,
  deleteTeacherMemoryToolDef,
  handleDeleteTeacherMemory,
  deleteTeacherMemoryAvailability,
  sendAssignmentConfirmationAvailability,
  sendAssignmentConfirmationMessageToolDef,
  updateOpenTaskAvailability,
  updateOpenTaskToolDef,
  downloadMsGsToolDef,
  handleDownloadMsGs,
  downloadMsGsAvailability,
  downloadSummaryReportsToolDef,
  handleDownloadSummaryReports,
  downloadSummaryReportsAvailability,
  reprocessErroredSubmissionsToolDef,
  handleReprocessErroredSubmissions,
  reprocessErroredSubmissionsAvailability,
  changeSettingsToolDef,
  handleChangeSettings,
  changeSettingsAvailability,
} from './index.js';

interface ToolSchema {
  type: string;
  function: {
    name: string;
    description: string;
    parameters?: unknown;
  };
}

export interface ToolHandlerResult {
  content?: string;
  [key: string]: unknown;
}

/** `role` is the current session's role (see ToolAvailability below), for schemas that differ per role. */
export type SchemaBuilder = (examState: ExamState, role?: 'teacher' | 'student') => ToolSchema;

export type ToolHandler = (exam: Exam, toolCallArgs: any) => Promise<ToolHandlerResult | object>;

/**
 * Whether a tool is offered for the current exam. Most tools decide from the exam state alone; the
 * optional `exam` is there for the ones whose condition lives in the task tree — "is the master solution
 * complete?" cannot be answered from `ExamState`, which carries progress but not tasks. It is optional so
 * the existing `(examState) => boolean` predicates stay valid unchanged, and because there is not always
 * an exam (pure chat). A tool whose condition needs the exam should default to being OFFERED when it is
 * absent: hiding a tool on incomplete information is worse than offering one that then explains itself.
 *
 * `role` is the CURRENT SESSION's role ('teacher' | 'student', see AgentStreamingLoopParams), also
 * optional and for the same reason: it is resolved once per turn by whichever caller already knows it
 * (agent-loop.ts has it in `params.role`; prompt-suggestion.ts resolves it once via
 * `resolveIsStudentOwnedExam` before calling this) rather than by this predicate reaching back into the
 * `Student` collection itself — that lookup is async and this whole chain, down to `Toolbox.getAvailableTools`'s
 * `Array.filter`, is not. A predicate that ignores the third argument (every existing one) stays valid
 * unchanged, same as `exam` being optional above.
 */
type ToolAvailability = (examState: ExamState, exam?: Exam, role?: 'teacher' | 'student') => boolean;

export class Tool {
  name: string;
  definition: ToolSchema | SchemaBuilder;
  handlingFunc: ToolHandler;
  available: ToolAvailability;

  constructor(
    name: string,
    definition: ToolSchema | SchemaBuilder,
    handlingFunc: ToolHandler,
    available: ToolAvailability,
  ) {
    this.name = name;
    this.definition = definition;
    this.handlingFunc = handlingFunc;
    this.available = available;
  }

  getName(): string {
    return this.name;
  }

  getDefinition(examState: ExamState, role?: 'teacher' | 'student'): ToolSchema {
    if (typeof this.definition === 'function') {
      return this.definition(examState, role);
    }
    return this.definition;
  }

  getDescription(examState: ExamState): string {
    return this.getDefinition(examState).function.description;
  }
}

export class Toolbox {
  private readonly tools: Tool[] = [];

  private parseToolSchema(toolSchema: ToolSchema | string): ToolSchema {
    const parsed = typeof toolSchema === 'string' ? JSON.parse(toolSchema) : toolSchema;

    if (!parsed || typeof parsed !== 'object') {
      throw new Error('Invalid tool schema: expected an object');
    }

    if (parsed.type !== 'function' || !parsed.function || typeof parsed.function !== 'object') {
      throw new Error('Invalid tool schema: expected { type: "function", function: {...} }');
    }

    if (typeof parsed.function.name !== 'string' || !parsed.function.name.trim()) {
      throw new Error('Invalid tool schema: missing function.name');
    }

    if (typeof parsed.function.description !== 'string' || !parsed.function.description.trim()) {
      throw new Error('Invalid tool schema: missing function.description');
    }

    return parsed as ToolSchema;
  }

  addTool(
    name: string,
    toolSchema: ToolSchema | SchemaBuilder,
    handlingFunc: ToolHandler,
    available: ToolAvailability,
  ): void {
    if (this.tools.some(t => t.name === name)) {
      throw new Error(`Tool with name "${name}" already registered`);
    }

    // If a static schema is provided, validate it now and ensure name consistency
    if (typeof toolSchema !== 'function') {
      const parsed = this.parseToolSchema(toolSchema);
      if (parsed.function.name !== name) {
        throw new Error(`Tool schema name mismatch: provided "${name}", schema has "${parsed.function.name}"`);
      }
    }

    const toolInstance = new Tool(name, toolSchema, handlingFunc, available);
    this.tools.push(toolInstance);
  }

  getAvailableTools(examState: ExamState, exam?: Exam, role?: 'teacher' | 'student'): Tool[] {
    return this.tools.filter(tool => tool.available(examState, exam, role));
  }

  getAvailableToolSchemas(examState: ExamState, exam?: Exam, role?: 'teacher' | 'student'): ToolSchema[] {
    return this.getAvailableTools(examState, exam, role).map(tool => tool.getDefinition(examState, role));
  }

  getToolSchemas(examState: ExamState): ToolSchema[] {
    return this.tools.map(tool => tool.getDefinition(examState));
  }

  getToolByName(name: string): Tool | undefined {
    return this.tools.find(tool => tool.getName() === name);
  }

  getDescByToolName(name: string, language?: string): string {
    // `language` is the conversation language and may be a label ('Deutsch') or a code ('de').
    // Resolve it to a supported i18n code so the fallback step is localized; without `lng`,
    // i18next.t would resolve against the server's global language (English) regardless of conversation.
    const raw = (language ?? '').trim();
    const lng = raw
      ? SUPPORTED_LANGUAGES.find(l => l.label === raw || l.value === raw || l.value === raw.toLowerCase())?.value
      : undefined;

    const localized = String(i18next.t(`toolFallbackDesc.${name}`, { defaultValue: '', lng })).trim();
    if (localized.length > 0) return localized;

    return String(
      i18next.t('toolFallbackDesc.default', {
        defaultValue: 'Toolschritt ausfuehren',
        lng,
      }),
    ).trim();
  }

  async executeTool(name: string, exam: Exam, toolCallArgs: any): Promise<object> {
    const tool = this.getToolByName(name);
    if (!tool) {
      throw new Error(`Tool with name "${name}" not found`);
    }

    return tool.handlingFunc(exam, toolCallArgs);
  }
}

// when adding a new tool, also add it to the createToolbox function below to make it available in the system
// remember to add a short description in all supported languages in the i18n translation files under the "toolFallbackDesc" namespace,
// using the tool's name as key (e.g. "metadata_tool": "Fetch exam metadata") - this description will be used if the tool doesn't provide its own description
// or if the description is missing for some reason
export function createToolbox(): Toolbox {
  const toolbox = new Toolbox();

  for (const tool of metadataToolDef) {
    toolbox.addTool(tool.function.name, tool, handleMetadata, metadataAvailability);
  }
  for (const tool of masterSolutionToolDef) {
    toolbox.addTool(tool.function.name, tool, handleGenerateMasterSolution, masterSolutionAvailability);
  }
  for (const tool of refineMasterSolutionToolDef) {
    toolbox.addTool(tool.function.name, tool, handleRefineMasterSolution, refineMasterSolutionAvailability);
  }
  for (const tool of gradingSchemeToolDef) {
    toolbox.addTool(tool.function.name, tool, handleGenerateGradingScheme, gradingSchemeAvailability);
  }
  for (const tool of refineGradingSchemeToolDef) {
    toolbox.addTool(tool.function.name, tool, handleRefineGradingScheme, refineGradingSchemeAvailability);
  }
  for (const tool of gradingAgentToolDef) {
    toolbox.addTool(tool.function.name, tool, handleRunGradingAgent, gradingAgentAvailability);
  }
  for (const tool of setTaskScoreToolDef) {
    toolbox.addTool(tool.function.name, tool, handleSetTaskScore, setTaskScoreAvailability);
  }
  for (const tool of openCanvasToolDef) {
    toolbox.addTool(tool.function.name, tool, handleOpenCanvas, openCanvasAvailability);
  }
  for (const tool of createExamToolDef) {
    toolbox.addTool(tool.function.name, tool, handleCreateExam, createExamAvailability);
  }
  for (const tool of confirmExamCreationToolDef) {
    toolbox.addTool(tool.function.name, tool, handleConfirmExamCreation, confirmExamCreationAvailability);
  }
  for (const tool of updateOpenTaskToolDef) {
    toolbox.addTool(tool.function.name, tool, handleUpdateOpenTaskContent, updateOpenTaskAvailability);
  }
  // sendAssignmentConfirmationMessageToolDef is a SchemaBuilder (function) that returns a single schema
  toolbox.addTool(
    'send_assignment_confirmation_message',
    sendAssignmentConfirmationMessageToolDef,
    handleSendAssignmentMessage,
    sendAssignmentConfirmationAvailability,
  );
  // processFilesToolDef is a SchemaBuilder (function): what an upload can still become depends on
  // how far the exam has progressed, so the schema is built per turn from the exam state.
  toolbox.addTool(PROCESS_FILES_TOOL_NAME, processFilesToolDef, handleProcessFiles, processFilesAvailability);
  for (const tool of confirmSplitInChatToolDef) {
    toolbox.addTool(tool.function.name, tool, handleConfirmSplitInChat, confirmSplitInChatAvailability);
  }
  for (const tool of confirmStructureInChatToolDef) {
    toolbox.addTool(tool.function.name, tool, handleConfirmStructureInChat, confirmStructureInChatAvailability);
  }
  for (const tool of queryToolsDef) {
    toolbox.addTool(tool.function.name, tool, handleQueryTool(tool.function.name), queryToolAvailability);
  }
  for (const tool of taskTypeToolDef) {
    toolbox.addTool(tool.function.name, tool, handleGetTaskTypes, taskTypeToolAvailability);
  }
  for (const tool of downloadMsGsToolDef) {
    toolbox.addTool(tool.function.name, tool, handleDownloadMsGs, downloadMsGsAvailability);
  }
  for (const tool of downloadSummaryReportsToolDef) {
    toolbox.addTool(tool.function.name, tool, handleDownloadSummaryReports, downloadSummaryReportsAvailability);
  }
  for (const tool of saveTeacherMemoryToolDef) {
    toolbox.addTool(tool.function.name, tool, handleSaveTeacherMemory, saveTeacherMemoryAvailability);
  }
  for (const tool of getTeacherMemoryToolDef) {
    toolbox.addTool(tool.function.name, tool, handleGetTeacherMemory, getTeacherMemoryAvailability);
  }
  for (const tool of deleteTeacherMemoryToolDef) {
    toolbox.addTool(tool.function.name, tool, handleDeleteTeacherMemory, deleteTeacherMemoryAvailability);
  }
  for (const tool of reprocessErroredSubmissionsToolDef) {
    toolbox.addTool(
      tool.function.name,
      tool,
      handleReprocessErroredSubmissions,
      reprocessErroredSubmissionsAvailability,
    );
  }
  // changeSettingsToolDef is a SchemaBuilder: grading_kindness is only offered to teachers.
  toolbox.addTool('change_settings', changeSettingsToolDef, handleChangeSettings, changeSettingsAvailability);

  return toolbox;
}
